import React, { useCallback, useEffect, useRef, memo } from 'react';
import PropTypes from 'prop-types';
import { Map, List, fromJS } from 'immutable';
import { createPortal } from 'react-dom';
import classNames from 'classnames';

import uniqueId from '../../../utils/uniqueId';
import debounce, { passFirst } from '../../../utils/debounce';
import AutoComplete from '../AutoComplete';
import { noop } from '../../../utils/noop';

import style from './style.module.css';

const handleGetSuggestionValue = suggestion => suggestion?.value ?? '';

const handleRenderSuggestion = suggestion => <div>{suggestion?.value ?? ''}</div>;

const ActivityDropdown = ({ activities, info, isSinglePanel = false, onChange, onNext, tabIndex, disabled }) => {
  const inputIdRef = useRef(uniqueId());
  const inputRef = useRef(null);
  const panelRef = useRef(null);

  const inputId = inputIdRef.current;

  const handleActivityChange = useCallback(
    debounce(isDropdownPanelOpen => {
      onChange({ isDropdownPanelOpen });
    }, 100),
    [onChange],
  );

  const updatePosition = useCallback(
    passFirst(() => {
      // force re-render by triggering a state-less update; kept minimal
    }, 100),
    [],
  );

  useEffect(() => {
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition);
    inputRef.current = document.getElementById(inputId);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    };
  }, [inputId, updatePosition]);

  const getEqualsActivity = useCallback(
    possibleActivity => {
      const value = String(possibleActivity).toLowerCase();

      return activities.find(activity => activity.get('value').toLowerCase() === value, false);
    },
    [activities],
  );

  const handleGetSuggestion = useCallback(
    ({ value }) => {
      const suggestions = activities.filter(
        activity =>
          activity
            .get('value')
            .toLowerCase()
            .indexOf(value.toLowerCase()) > -1,
      );

      onChange({ suggestions });
    },
    [activities],
  );

  const handleUpdateSuggestions = useCallback(
    update => {
      const activity = getEqualsActivity(update.text);

      if (activity) {
        update.selected = activity.toJS();
      }
      onChange(update);
    },
    [getEqualsActivity],
  );

  const handleSuggestionSelected = useCallback((event, { suggestion }) => {
    onChange({
      suggestions: [],
      selected: suggestion,
      text: suggestion.value,
    });
  }, []);

  const handleDropdownButtonClick = useCallback(
    event => {
      event.stopPropagation();

      if (!disabled) {
        handleActivityChange(!info.get('isDropdownPanelOpen'));
      }
    },
    [disabled, info, handleActivityChange],
  );

  const handleActivityClick = useCallback(
    event => {
      const text = event.target.innerText;
      const selected = activities.filter(activity => activity.get('value').toLowerCase() === text.toLowerCase()).get(0);

      onChange({ text, selected });
      handleActivityChange(false);
    },
    [activities, handleActivityChange],
  );

  const handlePortalClose = useCallback(
    event => {
      if (event) {
        if (event.relatedTarget === null) return;
        if (event.target !== event.currentTarget) return;
      }
      handleActivityChange(false);
    },
    [handleActivityChange],
  );

  const handleKeyPress = useCallback(
    event => {
      if (event.key === 'Enter' && onNext) {
        onNext();
      }
    },
    [onNext],
  );

  const handleSetPanelElement = useCallback(panel => {
    panelRef.current = panel;
  }, []);

  const getPanelStyle = useCallback(() => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();

      return {
        display: 'block',
        left: rect.left,
        top: rect.bottom + window.pageYOffset,
      };
    }

    return { display: 'none' };
  }, []);

  const renderGroupContent = useCallback(
    groupIndex =>
      activities
        .filter(item => item.get('group') === groupIndex)
        .map(item => {
          const disable = item.get('disable', false);

          return (
            <li
              key={item.get('value')}
              onClick={disable ? noop : handleActivityClick}
              style={disable ? { color: '#CCC' } : null}
              value={item.get('value')}
            >
              {item.get('value')}
            </li>
          );
        }),
    [activities, handleActivityClick],
  );

  const renderActivityDropdownPortal = () => {
    const isDropdownPanelOpen = info.get('isDropdownPanelOpen', false);

    const panelClass = classNames(style.panel, {
      hide: !isDropdownPanelOpen,
    });
    const panelCategoryClass = classNames(style.category, {
      hide: isSinglePanel,
    });
    const panelTitleClass = classNames({
      hide: isSinglePanel,
    });

    const container = (
      <div ref={handleSetPanelElement} className={panelClass} style={getPanelStyle()}>
        <div className={style.category}>
          <h4 className={panelTitleClass}>process</h4>
          <div className={style.content}>
            <ul className={style.group}>
              {renderGroupContent(1)}
              <li>&nbsp;</li>
              {renderGroupContent(7)}
            </ul>
            <ul className={style.group}>{renderGroupContent(2)}</ul>
            <ul className={style.group}>{renderGroupContent(3)}</ul>
            <ul className={style.group}>{renderGroupContent(4)}</ul>
            <ul className={style.group}>{renderGroupContent(5)}</ul>
            <ul className={style.group}>{renderGroupContent(6)}</ul>
          </div>
        </div>
        <div className={panelCategoryClass}>
          <h4>
            general <br />
            communication
          </h4>
          <div className={style.content}>
            <ul className={style.group}>{renderGroupContent(8)}</ul>
          </div>
        </div>
        <div className={panelCategoryClass}>
          <h4>lead progression</h4>
          <div className={style.content}>
            <ul className={style.group}>{renderGroupContent(9)}</ul>
          </div>
        </div>
        <div className={panelCategoryClass}>
          <h4>other</h4>
          <div className={style.content}>
            <ul className={style.group}>
              {renderGroupContent(10)}
              <li>&nbsp;</li>
              {renderGroupContent(11)}
            </ul>
          </div>
        </div>
      </div>
    );
    const portal = createPortal(container, document.querySelector('body'));

    return info.get('isDropdownPanelOpen') && portal;
  };

  const suggestions = info.get('suggestions', List());
  const sections = fromJS([
    {
      name: 'Default Section',
      section: suggestions,
    },
  ]);
  const text = info.get('text', '');
  const suggestionProps = {
    inputProps: {
      className: 'form-control',
      id: inputId,
      name: 'activityDropdown',
      disabled,
      placeholder: '',
      onKeyPress: handleKeyPress,
    },
    suggestions: sections,
    checkOn: 'text',
    onUpdateSuggestions: handleUpdateSuggestions,
    getSuggestion: handleGetSuggestion,
    getSuggestionValue: handleGetSuggestionValue,
    renderSuggestion: handleRenderSuggestion,
    onSuggestionSelected: handleSuggestionSelected,
    text,
    multiSection: true,
    renderSectionTitle: () => null,
    getSectionSuggestions: list => list.section,
  };

  if (tabIndex) {
    suggestionProps.inputProps.tabIndex = tabIndex;
  }

  const dropdownButton = classNames({
    'input-group-addon': true,
    disabled,
  });

  return (
    <>
      <div>
        <label className="field-label">Activity</label>
      </div>
      <AutoComplete {...suggestionProps}>
        <span
          className={dropdownButton}
          onBlur={handlePortalClose}
          onClick={handleDropdownButtonClick}
          style={{ outline: 0 }}
          tabIndex="-1"
        >
          <i aria-hidden="true" className="fa fa-caret-down" />
        </span>
      </AutoComplete>
      {renderActivityDropdownPortal()}
    </>
  );
};

ActivityDropdown.propTypes = {
  activities: PropTypes.instanceOf(List).isRequired,
  info: PropTypes.instanceOf(Map).isRequired,
  isSinglePanel: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onNext: PropTypes.func,
};

export default memo(ActivityDropdown);
